import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useStore } from '../store/store'
import { fmtPctValue, fmtSigned } from '../lib/format'
import { Card, EmptyState, PageHead, Pill, SelectField, type Tone } from '../components/ui'

const LEVEL_TONE: Record<string, Tone> = { low: 'green', elevated: 'amber', high: 'amber', severe: 'red' }
type SortKey = 'recent' | 'safest' | 'return' | 'riskiest'

export function StrategyLab() {
  const strategies = useStore((s) => s.strategies)
  const bury = useStore((s) => s.buryStrategy)
  const revive = useStore((s) => s.reviveStrategy)
  const remove = useStore((s) => s.removeStrategy)
  const [sort, setSort] = useState<SortKey>('recent')

  const candidates = useMemo(() => {
    const list = strategies.filter((s) => s.status !== 'buried')
    const sorted = [...list]
    if (sort === 'safest') sorted.sort((a, b) => a.overfit.score - b.overfit.score)
    else if (sort === 'riskiest') sorted.sort((a, b) => b.overfit.score - a.overfit.score)
    else if (sort === 'return') sorted.sort((a, b) => b.metrics.totalReturnPct - a.metrics.totalReturnPct)
    else sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    return sorted
  }, [strategies, sort])

  const graveyard = strategies.filter((s) => s.status === 'buried')

  const buryWithPrompt = (id: string, level: string) => {
    const note = window.prompt('Autopsy — why bury this strategy?', 'Failed evaluation.')
    if (note !== null) bury(id, `Overfit risk ${level}`, note)
  }

  return (
    <div className="stack">
      <PageHead title="Strategy Lab">
        Compare every evaluated strategy side by side, and keep a graveyard of the ideas you killed — with autopsies — so you never resurrect the same mistake.
      </PageHead>

      <Card
        title="Strategy comparison"
        subtitle="Lower overfit risk and higher walk-forward efficiency beat a bigger backtest number"
        action={
          <SelectField
            label=""
            value={sort}
            onChange={(v: SortKey) => setSort(v)}
            options={[
              { value: 'recent', label: 'Most recent' },
              { value: 'safest', label: 'Safest first' },
              { value: 'riskiest', label: 'Riskiest first' },
              { value: 'return', label: 'Best return' },
            ]}
          />
        }
      >
        {candidates.length === 0 ? (
          <EmptyState>
            No saved strategies yet. Evaluate one on the <Link to="/evaluation">Strategy Evaluation</Link> page and save it as a candidate.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Name</th><th>Asset</th><th>Overfit risk</th><th>Full return</th><th>OOS eff.</th><th>P(profit)</th><th>PBO</th><th>Trades</th><th></th></tr>
              </thead>
              <tbody>
                {candidates.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}<div className="faint" style={{ fontSize: 11 }}>{s.strategyType} · {Object.entries(s.params).map(([k, v]) => `${k}=${v}`).join(' ')}</div></td>
                    <td><span className="tag">{s.assetSymbol}</span></td>
                    <td><Pill tone={LEVEL_TONE[s.overfit.level]}>{s.overfit.score.toFixed(0)} {s.overfit.level}</Pill></td>
                    <td className={`mono ${s.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(s.metrics.totalReturnPct, 1)}%</td>
                    <td className={`mono ${s.wfEfficiency < 0.5 ? 'neg' : 'pos'}`}>{fmtPctValue(s.wfEfficiency * 100, 0)}</td>
                    <td className={`mono ${s.resample.pProfit < 0.6 ? 'neg' : 'pos'}`}>{fmtPctValue(s.resample.pProfit * 100, 0)}</td>
                    <td className={`mono ${s.pbo > 0.5 ? 'neg' : ''}`}>{fmtPctValue(s.pbo * 100, 0)}</td>
                    <td className={`mono ${s.metrics.trades < 10 ? 'neg' : ''}`}>{s.metrics.trades}</td>
                    <td className="nowrap">
                      <button className="btn sm" onClick={() => buryWithPrompt(s.id, s.overfit.level)}>Bury</button>{' '}
                      <button className="btn sm danger" onClick={() => remove(s.id)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={'\u26B0 Strategy Graveyard'} subtitle="Here lie the ideas that did not survive. Rest in peace (and read the autopsy).">
        {graveyard.length === 0 ? (
          <EmptyState>The graveyard is empty. Killing a bad idea is a win — bury one when it fails evaluation.</EmptyState>
        ) : (
          <div className="grid cols-3">
            {graveyard.map((s) => (
              <div key={s.id} className="card tight" style={{ borderColor: '#3a2730' }}>
                <div className="center" style={{ fontSize: 24 }}>{'\uD83E\uDEA6'}</div>
                <div className="center" style={{ fontWeight: 600 }}>{s.name}</div>
                <div className="center faint" style={{ fontSize: 11, marginBottom: 8 }}>{s.assetSymbol} · {s.strategyType}</div>
                <div className="kv"><span className="k">Cause of death</span><span className="v" style={{ fontSize: 11 }}>{s.causeOfDeath ?? '—'}</span></div>
                <div className="kv"><span className="k">Overfit</span><span className="v"><Pill tone={LEVEL_TONE[s.overfit.level]}>{s.overfit.score.toFixed(0)}</Pill></span></div>
                <div className="kv"><span className="k">Return</span><span className={`v ${s.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(s.metrics.totalReturnPct, 0)}%</span></div>
                {s.autopsy && <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>{'\u201C'}{s.autopsy}{'\u201D'}</p>}
                <div className="btn-row" style={{ marginTop: 8 }}>
                  <button className="btn sm" onClick={() => revive(s.id)}>Revive</button>
                  <button className="btn sm danger" onClick={() => remove(s.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
